import { supabase } from "@/integrations/supabase/client";

export type TimeFrame = 'daily' | 'weekly' | 'monthly' | 'custom';
export type MetricGrowth = {
  value: number;
  percentage: number;
  trend: 'up' | 'down' | 'neutral';
  currentValue?: number;
  previousValue?: number;
};

export type UserMetrics = {
  totalUsers: MetricGrowth;
  newUsers: MetricGrowth;
  userGrowthData: Array<{ date: string; users: number }>;
};

export type PromptMetrics = {
  totalCopies: MetricGrowth;
  totalSaves: MetricGrowth;
  totalCollections: MetricGrowth;
  allTimeCopies: number;      // All-time count for copies
  allTimeSaves: number;       // All-time count for saves
  allTimeCollections: number; // All-time count for collections
  interactionData: Array<{ date: string; copies: number; saves: number; collections: number }>;
  // Individual chart data for each metric
  copiesData: Array<{ date: string; copies: number }>;
  savesData: Array<{ date: string; saves: number }>;
  collectionsData: Array<{ date: string; collections: number }>;
};

export type RatingMetrics = {
  averageRating: MetricGrowth;
  totalReviews: number;
  ratingDistribution: Array<{ rating: number; count: number }>;
};

export type TopPrompt = {
  id: string;
  title: string;
  category: string;
  copies: number;
  saves: number;
  collections: number;
  rating: number;
};

export class MetricsService {
  // Helper to calculate growth percentage and trend
  private calculateGrowth(current: number, previous: number): { percentage: number; trend: 'up' | 'down' | 'neutral'; currentValue: number; previousValue: number } {
    if (previous === 0) return { percentage: 100, trend: 'up', currentValue: current, previousValue: 0 };
    
    const percentage = ((current - previous) / previous) * 100;
    const trend = percentage > 0 ? 'up' : percentage < 0 ? 'down' : 'neutral';
    
    return {
      percentage: Math.abs(Math.round(percentage * 10) / 10),
      trend,
      currentValue: current,
      previousValue: previous
    };
  }

  // Get date range based on time frame - making this public so it can be used elsewhere
  getDateRange(timeFrame: TimeFrame, customRange?: { start: Date; end: Date }) {
    const endDate = new Date();
    let startDate: Date;
    
    if (timeFrame === 'custom' && customRange) {
      return {
        start: customRange.start,
        end: customRange.end
      };
    }
    
    switch (timeFrame) {
      case 'daily':
        startDate = new Date();
        startDate.setDate(endDate.getDate() - 1);
        break;
      case 'weekly':
        startDate = new Date();
        startDate.setDate(endDate.getDate() - 7);
        break;
      case 'monthly':
      default:
        startDate = new Date();
        startDate.setMonth(endDate.getMonth() - 1);
    }
    
    // Reset time for the startDate to beginning of day
    startDate.setHours(0, 0, 0, 0);
    
    // Set time for endDate to end of day
    endDate.setHours(23, 59, 59, 999);
    
    console.log(`Date range for ${timeFrame}: ${startDate.toISOString()} to ${endDate.toISOString()}`);
    
    return { start: startDate, end: endDate };
  }

  // Get previous date range for comparison
  private getPreviousDateRange(timeFrame: TimeFrame, customRange?: { start: Date; end: Date }) {
    const currentRange = this.getDateRange(timeFrame, customRange);
    const rangeDuration = currentRange.end.getTime() - currentRange.start.getTime();
    
    const previousEnd = new Date(currentRange.start);
    previousEnd.setMilliseconds(previousEnd.getMilliseconds() - 1); // Set to right before current period
    
    const previousStart = new Date(previousEnd.getTime() - rangeDuration);
    
    console.log(`Previous date range: ${previousStart.toISOString()} to ${previousEnd.toISOString()}`);
    
    return { start: previousStart, end: previousEnd };
  }

  // Prepare date-based data for metrics with proper granularity
  private prepareDateData(timeFrame: TimeFrame, customRange?: { start: Date; end: Date }) {
    const currentRange = this.getDateRange(timeFrame, customRange);
    const dates = [];
    
    // Calculate number of data points and interval based on timeframe
    let numPoints = 0;
    let intervalMs = 0;
    
    switch (timeFrame) {
      case 'daily':
        numPoints = 24; // Hourly for daily view
        intervalMs = 60 * 60 * 1000; // 1 hour in ms
        break;
      case 'weekly':
        numPoints = 7; // Daily for weekly view
        intervalMs = 24 * 60 * 60 * 1000; // 1 day in ms
        break;
      case 'monthly':
        numPoints = 30; // Daily for monthly view
        intervalMs = 24 * 60 * 60 * 1000; // 1 day in ms
        break;
      case 'custom':
        if (customRange) {
          // For custom range, calculate appropriate interval and number of points
          const rangeDuration = customRange.end.getTime() - customRange.start.getTime();
          if (rangeDuration <= 86400000) { // Less than a day
            numPoints = 24; // Hourly
            intervalMs = 60 * 60 * 1000;
          } else if (rangeDuration <= 604800000) { // Less than a week
            numPoints = Math.ceil(rangeDuration / (24 * 60 * 60 * 1000)); // Daily
            intervalMs = 24 * 60 * 60 * 1000;
          } else {
            // For longer periods, aim for 15-30 data points
            numPoints = Math.min(30, Math.ceil(rangeDuration / (24 * 60 * 60 * 1000)));
            intervalMs = Math.ceil(rangeDuration / numPoints);
          }
        } else {
          // Default to monthly view if custom range is not provided
          numPoints = 30;
          intervalMs = 24 * 60 * 60 * 1000;
        }
        break;
    }
    
    // Generate dates
    const startTime = currentRange.start.getTime();
    for (let i = 0; i < numPoints; i++) {
      const date = new Date(startTime + (i * intervalMs));
      dates.push({
        date: date.toISOString().split('.')[0], // Remove milliseconds
        timestamp: date.getTime()
      });
    }
    
    return dates;
  }

  // Calculate cumulative user growth data over time using auth.users creation dates
  private calculateUserGrowthByTimeframe(userCreationDates: Array<{ created_at: string }>, datePoints: Array<any>) {
    console.log("Building user growth data with:", userCreationDates.length, "users and", datePoints.length, "data points");
    
    // Sort user data by creation date
    const sortedDates = [...userCreationDates].sort((a, b) => 
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
    
    const result = datePoints.map(point => {
      const pointDate = new Date(point.timestamp);
      
      // Count users created before or on this date point
      const usersCount = sortedDates.filter(user => {
        const createdAt = new Date(user.created_at);
        return createdAt <= pointDate;
      }).length;
      
      return {
        date: point.date,
        users: usersCount
      };
    });
    
    console.log("User growth data points:", result);
    return result;
  }

  // Calculate interactions metrics by time period - counts interactions within each time period (not cumulative)
  private calculateInteractionsByTimeframe(
    copyData: Array<any>, 
    savedData: Array<any>, 
    collectionsData: Array<any>, 
    datePoints: Array<any>
  ) {
    // Calculate interactions for each time period (not cumulative)
    const result = datePoints.map((point, index) => {
      const pointDate = new Date(point.timestamp);
      const nextPointDate = index < datePoints.length - 1 ? 
                            new Date(datePoints[index + 1].timestamp) : 
                            new Date(pointDate.getTime() + 86400000); // Add a day if it's the last point
      
      // Count interactions that happened within this specific time slot
      const copiesCount = copyData.filter(copy => {
        const createdAt = new Date(copy.created_at);
        return createdAt >= pointDate && createdAt < nextPointDate;
      }).length;
      
      const savesCount = savedData.filter(save => {
        const savedAt = new Date(save.saved_at);
        return savedAt >= pointDate && savedAt < nextPointDate;
      }).length;
      
      const collectionsCount = collectionsData.filter(collection => {
        const addedAt = new Date(collection.added_at);
        return addedAt >= pointDate && addedAt < nextPointDate;
      }).length;
      
      return {
        date: point.date,
        copies: copiesCount,
        saves: savesCount,
        collections: collectionsCount
      };
    });
    
    return result;
  }
  
  // Fixed method to prepare individual metric chart data
  private calculateIndividualMetricData<T extends 'copies' | 'saves' | 'collections'>(
    data: any[], 
    dateField: string,
    metricName: T,
    datePoints: Array<any>
  ): Array<{ date: string } & { [K in T]: number }> {
    const result = datePoints.map((point, index) => {
      const pointDate = new Date(point.timestamp);
      const nextPointDate = index < datePoints.length - 1 ? 
                          new Date(datePoints[index + 1].timestamp) : 
                          new Date(pointDate.getTime() + 86400000); // Add a day if it's the last point
      
      // Count items that happened within this specific time slot
      const count = data.filter(item => {
        const itemDate = new Date(item[dateField]);
        return itemDate >= pointDate && itemDate < nextPointDate;
      }).length;
      
      // Create a properly typed object with the correct structure
      const resultObj = {
        date: point.date
      } as { date: string } & { [key in T]: number };
      
      // Use type assertion to ensure TypeScript understands this assignment
      resultObj[metricName] = count as ({ date: string } & { [key in T]: number })[T];
      
      return resultObj;
    });
    
    return result;
  }

  // Helper to check if two dates are in the same time period
  private isSameTimePeriod(date1: Date, date2: Date, hourly: boolean) {
    if (hourly) {
      return date1.getFullYear() === date2.getFullYear() && 
             date1.getMonth() === date2.getMonth() && 
             date1.getDate() === date2.getDate() &&
             date1.getHours() === date2.getHours();
    } else {
      return date1.getFullYear() === date2.getFullYear() && 
             date1.getMonth() === date2.getMonth() && 
             date1.getDate() === date2.getDate();
    }
  }

  // Get user metrics using real user data when available
  async getUserMetrics(
    timeFrame: TimeFrame, 
    customRange?: { start: Date; end: Date }, 
    realUserCount?: number,
    userCreationDates?: any[],
    newUsersInPeriod?: number
  ): Promise<UserMetrics | null> {
    try {
      const currentRange = this.getDateRange(timeFrame, customRange);
      const previousRange = this.getPreviousDateRange(timeFrame, customRange);
      
      console.log("Current date range:", currentRange.start, "to", currentRange.end);
      console.log("Previous date range:", previousRange.start, "to", previousRange.end);
      
      // Only use real data, no mock data
      if (userCreationDates === undefined || userCreationDates.length === 0 || realUserCount === undefined || realUserCount === 0) {
        console.log("No real user data available");
        return null;
      }
      
      console.log("Processing user creation dates:", userCreationDates.length, "records");
      
      // Calculate new users based on the data from our RPC function
      const newUsers = newUsersInPeriod !== undefined ? newUsersInPeriod : userCreationDates.filter(user => {
        const createdAt = new Date(user.created_at);
        return createdAt >= currentRange.start && createdAt <= currentRange.end;
      }).length;
      
      console.log("New users in current range:", newUsers);
      
      // Calculate total users as of the end of the current range
      const totalUsers = realUserCount;
      
      // Calculate new users in the previous range for comparison
      const newUsersPrevious = userCreationDates.filter(user => {
        const createdAt = new Date(user.created_at);
        return createdAt >= previousRange.start && createdAt <= previousRange.end;
      }).length;
      
      console.log("New users in previous range:", newUsersPrevious);
      
      // Total users at the end of the previous period
      const totalUsersPrevious = userCreationDates.filter(user => {
        const createdAt = new Date(user.created_at);
        return createdAt <= previousRange.end;
      }).length;
      
      console.log("Total users at end of previous period:", totalUsersPrevious);
      
      // Generate user growth data points with proper granularity
      const datePoints = this.prepareDateData(timeFrame, customRange);
      const userGrowthData = this.calculateUserGrowthByTimeframe(userCreationDates, datePoints);
      
      // Calculate growth metrics
      const totalUserGrowth = this.calculateGrowth(totalUsers, totalUsersPrevious);
      const newUserGrowth = this.calculateGrowth(newUsers, newUsersPrevious);
      
      return {
        totalUsers: {
          value: totalUsers,
          percentage: totalUserGrowth.percentage,
          trend: totalUserGrowth.trend,
          currentValue: totalUsers,
          previousValue: totalUsersPrevious
        },
        newUsers: {
          value: newUsers,
          percentage: newUserGrowth.percentage,
          trend: newUserGrowth.trend,
          currentValue: newUsers,
          previousValue: newUsersPrevious
        },
        userGrowthData
      };
    } catch (error) {
      console.error("Error fetching user metrics:", error);
      return null;
    }
  }

  // Get prompt metrics using real data when available - Updated to include individual metric data
  async getPromptMetrics(
    timeFrame: TimeFrame, 
    customRange?: { start: Date; end: Date }, 
    copyData?: any[], 
    savedData?: any[],
    collectionsData?: any[]
  ): Promise<PromptMetrics | null> {
    try {
      const currentRange = this.getDateRange(timeFrame, customRange);
      const previousRange = this.getPreviousDateRange(timeFrame, customRange);
      
      // Only return data if we have real data
      if (!copyData || copyData.length === 0) {
        console.log("No real copy data available");
        return null;
      }
      
      if (!savedData || savedData.length === 0) {
        console.log("No real saved data available");
        return null;
      }
      
      if (!collectionsData || collectionsData.length === 0) {
        console.log("No real collections data available");
        return null;
      }
      
      console.log(`Processing interaction data for timeframe: ${timeFrame}`);
      console.log(`Current date range: ${currentRange.start.toISOString()} to ${currentRange.end.toISOString()}`);
      
      // Calculate all-time counts - these should always be the total, unfiltered by time period
      const allTimeCopies = copyData.length;
      const allTimeSaves = savedData.length;
      const allTimeCollections = collectionsData.length;
      
      console.log(`All-time metrics - Copies: ${allTimeCopies}, Saves: ${allTimeSaves}, Collections: ${allTimeCollections}`);
      
      // Detailed logging for debugging
      if (copyData.length > 0) {
        console.log("First 3 copy records:", copyData.slice(0, 3).map(copy => copy.created_at));
        console.log("Last 3 copy records:", copyData.slice(-3).map(copy => copy.created_at));
      }
      
      // Calculate metrics for current time period only
      const totalCopiesInPeriod = copyData.filter(copy => {
        const createdAt = new Date(copy.created_at);
        return createdAt >= currentRange.start && createdAt <= currentRange.end;
      }).length;
      
      const totalSavesInPeriod = savedData.filter(save => {
        const savedAt = new Date(save.saved_at);
        return savedAt >= currentRange.start && savedAt <= currentRange.end;
      }).length;
      
      const totalCollectionsInPeriod = collectionsData.filter(collection => {
        const addedAt = new Date(collection.added_at);
        return addedAt >= currentRange.start && addedAt <= currentRange.end;
      }).length;
      
      console.log(`Current period metrics - Copies: ${totalCopiesInPeriod}, Saves: ${totalSavesInPeriod}, Collections: ${totalCollectionsInPeriod}`);
      
      // Previous period metrics for comparison
      const totalCopiesPrevious = copyData.filter(copy => {
        const createdAt = new Date(copy.created_at);
        return createdAt >= previousRange.start && createdAt <= previousRange.end;
      }).length;
      
      const totalSavesPrevious = savedData.filter(save => {
        const savedAt = new Date(save.saved_at);
        return savedAt >= previousRange.start && savedAt <= previousRange.end;
      }).length;
      
      const totalCollectionsPrevious = collectionsData.filter(collection => {
        const addedAt = new Date(collection.added_at);
        return addedAt >= previousRange.start && addedAt <= previousRange.end;
      }).length;
      
      console.log(`Previous period metrics - Copies: ${totalCopiesPrevious}, Saves: ${totalSavesPrevious}, Collections: ${totalCollectionsPrevious}`);
      
      // Generate interaction data for chart using real data
      const datePoints = this.prepareDateData(timeFrame, customRange);
      
      // Get the combined interaction data
      const interactionData = this.calculateInteractionsByTimeframe(
        copyData, 
        savedData, 
        collectionsData, 
        datePoints
      );
      
      // Create individual chart data for each metric with proper typing
      const copiesData = this.calculateIndividualMetricData<'copies'>(
        copyData, 
        'created_at', 
        'copies', 
        datePoints
      );
      
      const savesData = this.calculateIndividualMetricData<'saves'>(
        savedData, 
        'saved_at', 
        'saves', 
        datePoints
      );
      
      const collectionsData2 = this.calculateIndividualMetricData<'collections'>(
        collectionsData, 
        'added_at', 
        'collections', 
        datePoints
      );
      
      // Calculate growth metrics based on current and previous periods
      const copiesGrowth = this.calculateGrowth(totalCopiesInPeriod, totalCopiesPrevious);
      const savesGrowth = this.calculateGrowth(totalSavesInPeriod, totalSavesPrevious);
      const collectionsGrowth = this.calculateGrowth(totalCollectionsInPeriod, totalCollectionsPrevious);
      
      return {
        // For metric cards with growth data, use period-specific data
        totalCopies: {
          value: totalCopiesInPeriod,
          percentage: copiesGrowth.percentage,
          trend: copiesGrowth.trend,
          currentValue: totalCopiesInPeriod,
          previousValue: totalCopiesPrevious
        },
        totalSaves: {
          value: totalSavesInPeriod,
          percentage: savesGrowth.percentage,
          trend: savesGrowth.trend,
          currentValue: totalSavesInPeriod,
          previousValue: totalSavesPrevious
        },
        totalCollections: {
          value: totalCollectionsInPeriod,
          percentage: collectionsGrowth.percentage,
          trend: collectionsGrowth.trend,
          currentValue: totalCollectionsInPeriod,
          previousValue: totalCollectionsPrevious
        },
        // Include all-time counts for display in metrics cards
        allTimeCopies,
        allTimeSaves,
        allTimeCollections,
        interactionData,
        // Add individual chart data
        copiesData,
        savesData,
        collectionsData: collectionsData2
      };
    } catch (error) {
      console.error("Error fetching prompt metrics:", error);
      return null;
    }
  }

  // Get rating metrics using real review data and feedback data
  async getRatingMetrics(
    timeFrame: TimeFrame, 
    customRange?: { start: Date; end: Date }, 
    reviewData?: any[],
    feedbackData?: any[]
  ): Promise<RatingMetrics | null> {
    try {
      const currentRange = this.getDateRange(timeFrame, customRange);
      const previousRange = this.getPreviousDateRange(timeFrame, customRange);
      
      // Check if we have any rating data (either reviews or feedback)
      const hasReviewData = reviewData && reviewData.length > 0;
      const hasFeedbackData = feedbackData && feedbackData.length > 0;
      
      if (!hasReviewData && !hasFeedbackData) {
        console.log("No real rating data available");
        return null;
      }
      
      // Filter reviews in current period
      const currentReviews = hasReviewData ? reviewData.filter(review => {
        const createdAt = new Date(review.created_at);
        return createdAt >= currentRange.start && createdAt <= currentRange.end;
      }) : [];
      
      // Filter feedback in current period
      const currentFeedback = hasFeedbackData ? feedbackData.filter(feedback => {
        const createdAt = new Date(feedback.created_at);
        return createdAt >= currentRange.start && createdAt <= currentRange.end;
      }) : [];
      
      // Filter reviews in previous period
      const previousReviews = hasReviewData ? reviewData.filter(review => {
        const createdAt = new Date(review.created_at);
        return createdAt >= previousRange.start && createdAt <= previousRange.end;
      }) : [];
      
      // Filter feedback in previous period
      const previousFeedback = hasFeedbackData ? feedbackData.filter(feedback => {
        const createdAt = new Date(feedback.created_at);
        return createdAt >= previousRange.start && createdAt <= previousRange.end;
      }) : [];
      
      // Combine reviews and feedback for current period
      const totalRatings = [...currentReviews, ...currentFeedback];
      const totalReviews = totalRatings.length;
      
      // If no ratings in the selected time frame, return null
      if (totalReviews === 0) {
        console.log("No ratings in the selected time frame");
        return null;
      }
      
      // Calculate average rating
      let averageRating = 0;
      if (totalReviews > 0) {
        const sum = totalRatings.reduce((total, item) => total + (item.rating || 0), 0);
        averageRating = sum / totalReviews;
      }
      
      // Combine reviews and feedback for previous period
      const totalPreviousRatings = [...previousReviews, ...previousFeedback];
      
      // Calculate previous average rating
      let previousAverageRating = 0;
      if (totalPreviousRatings.length > 0) {
        const previousSum = totalPreviousRatings.reduce((total, item) => total + (item.rating || 0), 0);
        previousAverageRating = previousSum / totalPreviousRatings.length;
      }
      
      // Calculate rating distribution
      const ratingDistribution = [
        { rating: 1, count: 0 },
        { rating: 2, count: 0 },
        { rating: 3, count: 0 },
        { rating: 4, count: 0 },
        { rating: 5, count: 0 }
      ];
      
      for (let i = 1; i <= 5; i++) {
        ratingDistribution[i - 1].count = totalRatings.filter(item => Math.round(item.rating) === i).length;
      }
      
      const ratingGrowth = this.calculateGrowth(averageRating, previousAverageRating);
      
      return {
        averageRating: {
          value: averageRating,
          percentage: ratingGrowth.percentage,
          trend: ratingGrowth.trend,
          currentValue: averageRating,
          previousValue: previousAverageRating
        },
        totalReviews,
        ratingDistribution
      };
    } catch (error) {
      console.error("Error fetching rating metrics:", error);
      return null;
    }
  }

  // Get top performing prompts with real data
  async getTopPrompts(
    timeFrame: TimeFrame, 
    limit: number = 10, 
    customRange?: { start: Date; end: Date }
  ): Promise<TopPrompt[]> {
    try {
      // Try to fetch real prompt data
      const { data: realPrompts, error } = await supabase
        .from('prompts')
        .select('id, title, category_id, copies, likes')
        .order('copies', { ascending: false })
        .limit(limit);
        
      if (error || !realPrompts || realPrompts.length === 0) {
        console.log("No real prompt data available");
        return [];
      }
      
      // We have real data, get category names
      const categoryIds = [...new Set(realPrompts.map(p => p.category_id))];
      
      const { data: categories } = await supabase
        .from('categories')
        .select('id, name')
        .in('id', categoryIds);
        
      const categoryMap = new Map();
      if (categories) {
        categories.forEach(cat => categoryMap.set(cat.id, cat.name));
      }
      
      // Get saves data for each prompt from user_saved_prompts
      const promptIds = realPrompts.map(p => p.id);
      
      const { data: savedPrompts } = await supabase
        .from('user_saved_prompts')
        .select('prompt_id')
        .in('prompt_id', promptIds);
    
      // Count saved prompts by prompt_id manually
      const savesMap = new Map();
      if (savedPrompts && savedPrompts.length > 0) {
        savedPrompts.forEach(item => {
          const currentCount = savesMap.get(item.prompt_id) || 0;
          savesMap.set(item.prompt_id, currentCount + 1);
        });
      }
      
      // Get collections data for each prompt from collection_prompts
      const { data: collectionPrompts } = await supabase
        .from('collection_prompts')
        .select('saved_prompt_id');
        
      // Get mapping of saved prompts to original prompts
      const { data: userSavedPrompts } = await supabase
        .from('user_saved_prompts')
        .select('id, prompt_id');
        
      // Map saved_prompt_ids to original prompt_ids
      const savedPromptToOriginalMap = new Map();
      if (userSavedPrompts) {
        userSavedPrompts.forEach(sp => {
          savedPromptToOriginalMap.set(sp.id, sp.prompt_id);
        });
      }
      
      // Count collections by prompt ID
      const collectionsMap = new Map();
      if (collectionPrompts && userSavedPrompts) {
        collectionPrompts.forEach(cp => {
          const originalPromptId = savedPromptToOriginalMap.get(cp.saved_prompt_id);
          if (originalPromptId) {
            const count = collectionsMap.get(originalPromptId) || 0;
            collectionsMap.set(originalPromptId, count + 1);
          }
        });
      }
      
      // Get average ratings for each prompt from reviews table
      const { data: ratingData } = await supabase
        .from('reviews')
        .select('prompt_id, rating')
        .in('prompt_id', promptIds);
        
      const ratingsMap = new Map();
      const ratingsCountMap = new Map();
      
      if (ratingData) {
        ratingData.forEach(rating => {
          const promptId = rating.prompt_id;
          const currentSum = ratingsMap.get(promptId) || 0;
          const currentCount = ratingsCountMap.get(promptId) || 0;
          
          ratingsMap.set(promptId, currentSum + rating.rating);
          ratingsCountMap.set(promptId, currentCount + 1);
        });
      }
      
      return realPrompts.map(prompt => {
        // Calculate average rating if we have ratings data
        let rating = 0;
        if (ratingsCountMap.get(prompt.id)) {
          rating = ratingsMap.get(prompt.id) / ratingsCountMap.get(prompt.id);
        } else {
          // Estimate based on likes if no ratings
          rating = ((prompt.likes || 0) / 20) + 3.5;
        }
        
        return {
          id: prompt.id,
          title: prompt.title,
          category: categoryMap.get(prompt.category_id) || 'Uncategorized',
          copies: prompt.copies || 0,
          saves: savesMap.get(prompt.id) || 0,
          collections: collectionsMap.get(prompt.id) || 0,
          rating: Number(rating.toFixed(1))
        };
      });
    } catch (error) {
      console.error("Error fetching top prompts:", error);
      return [];
    }
  }
}

export const metricsService = new MetricsService();
